import React, { useEffect } from 'react'
import styled, { css, DefaultTheme, keyframes } from 'styled-components'
import { Dict } from '../../../globalTypes'
import { defaultTheme } from '../../../theme'
import { Button } from '../../buttons/Button/Button'
import { IconCross } from '../../iconography/Icons/Icons'

const calculatePercents = (totalDuration: number) => {
  let timeInPercents = (300 / totalDuration) * 100
  if (timeInPercents > 25) timeInPercents = 25

  return timeInPercents
}

const appearRight = keyframes`
  0% {
    transform: translateX(calc(100% + 96px))
  }

  100% {
    transform: translateX(0);
  }
`

const appearAndDisappearRight = (totalDuration: number) => {
  const timeInPercents = calculatePercents(totalDuration)

  return keyframes`
  0% {
    transform: translateX(calc(100% + 96px))
  }

  ${timeInPercents}% {
    transform: translateX(0);
  }

  ${100 - timeInPercents}% {
    transform: translateX(0);
  }

  100% {
    transform: translateX(calc(100% + 96px))
  }
`
}

const appearLeft = keyframes`
  0% {
    transform: translateX(calc(-100% - 96px))
  }

  100% {
    transform: translateX(0);
  }
`

const appearAndDisappearLeft = (totalDuration: number) => {
  const timeInPercents = calculatePercents(totalDuration)

  return keyframes`
  0% {
    transform: translateX(calc(-100% - 96px))
  }

  ${timeInPercents}% {
    transform: translateX(0);
  }

  ${100 - timeInPercents}% {
    transform: translateX(0);
  }

  100% {
    transform: translateX(calc(-100% - 96px))
  }
`
}

const appearTop = keyframes`
  0% {
    transform: translateX(-50%) translateY(calc(-100% - 96px))
  }

  100% {
    transform: translateX(-50%) translateY(0);
  }
`

const appearAndDisappearTop = (totalDuration: number) => {
  const timeInPercents = calculatePercents(totalDuration)

  return keyframes`
  0% {
    transform: translateX(-50%) translateY(calc(-100% - 96px))
  }

  ${timeInPercents}% {
    transform: translateX(-50%) translateY(0);
  }

  ${100 - timeInPercents}% {
    transform: translateX(-50%) translateY(0);
  }

  100% {
    transform: translateX(-50%) translateY(calc(-100% - 96px))
  }
`
}

const appearBottom = keyframes`
  0% {
    transform: translateX(-50%) translateY(calc(100% + 96px))
  }

  100% {
    transform: translateX(-50%) translateY(0);
  }
`

const appearAndDisappearBottom = (totalDuration: number) => {
  let timeInPercents = (300 / totalDuration) * 100
  if (timeInPercents > 25) timeInPercents = 25

  return keyframes`
  0% {
    transform: translateX(-50%) translateY(calc(100% + 96px))
  }

  ${timeInPercents}% {
    transform: translateX(-50%) translateY(0);
  }

  ${100 - timeInPercents}% {
    transform: translateX(-50%) translateY(0);
  }

  100% {
    transform: translateX(-50%) translateY(calc(100% + 96px))
  }
`
}

const Wrapper = styled.div`
  position: absolute;
  z-index: ${defaultTheme.zIndex.snackbar};
`

const severityToColor: Dict<keyof DefaultTheme['color']> = {
  error: 'accentDestruct500',
  warning: 'accentNotify600',
  info: 'secondary500',
  success: 'accentApprove600',
}

export type SnackbarProps = React.HTMLAttributes<HTMLDivElement> & {
  onClose?: () => void
  open?: boolean
  autoHideDuration?: number
  onHide?: () => void
  severity?: 'error' | 'warning' | 'info' | 'success'
  hideCloseButton?: boolean
  marginHorizontal?: string
  marginVertical?: string
  position?: 'topLeft' | 'topRight' | 'bottomLeft' | 'bottomRight' | 'topCenter' | 'bottomCenter'
}

const getPositionCSS = (
  open: boolean,
  marginHorizontal: string,
  marginVertical: string,
  autoHideDuration: number | undefined,
  position: SnackbarProps['position'],
) => {
  const animationFunc = 'cubic-bezier(0.175, 0.885, 0.32, 1.275)'
  const animationDuration = autoHideDuration ? `${autoHideDuration}ms` : '0.3s'
  switch (position) {
    case 'topLeft':
      return css`
        top: ${marginVertical};
        left: ${marginHorizontal};
        transform: translateX(calc(-100% - 96px));
        animation: ${open
          ? css`${autoHideDuration ? appearAndDisappearLeft(autoHideDuration) : appearTop} ${animationDuration}
          ${animationFunc} forwards}`
          : 'none'};
      `
    case 'topRight':
      return css`
        top: ${marginVertical};
        right: ${marginHorizontal};
        transform: translateX(calc(100% + 96px));
        animation: ${open
          ? css`${autoHideDuration ? appearAndDisappearRight(autoHideDuration) : appearRight} ${animationDuration}
          ${animationFunc} forwards}`
          : 'none'};
      `
    case 'topCenter':
      return css`
        top: ${marginVertical};
        left: 50%;
        transform: translateX(-50%) translateY(calc(-100% - 96px));
        max-width: 500px;
        animation: ${open
          ? css`${autoHideDuration ? appearAndDisappearTop(autoHideDuration) : appearTop} ${animationDuration}
          ${animationFunc} forwards}`
          : 'none'};
      `
    case 'bottomLeft':
      return css`
        bottom: ${marginVertical};
        left: ${marginHorizontal};
        transform: translateX(calc(-100% - 96px));
        animation: ${open
          ? css`${autoHideDuration ? appearAndDisappearLeft(autoHideDuration) : appearLeft} ${animationDuration}
          ${animationFunc} forwards}`
          : 'none'};
      `
    case 'bottomRight':
      return css`
        bottom: ${marginVertical};
        right: ${marginHorizontal};
        transform: translateX(calc(100% + 96px));
        animation: ${open
          ? css`${autoHideDuration ? appearAndDisappearRight(autoHideDuration) : appearRight} ${animationDuration}
          ${animationFunc} forwards}`
          : 'none'};
      `
    case 'bottomCenter':
      return css`
        bottom: ${marginVertical};
        left: 50%;
        transform: translateX(-50%) translateY(calc(100% + 96px));
        max-width: 500px;
        animation: ${open
          ? css`${autoHideDuration ? appearAndDisappearBottom(autoHideDuration) : appearBottom} ${animationDuration}
          ${animationFunc} forwards}`
          : 'none'};
      `
    case 'bottomRight':
    default:
      return css`
        bottom: ${marginVertical};
        right: ${marginHorizontal};
        animation: ${open
          ? css`${autoHideDuration ? appearAndDisappearRight(autoHideDuration) : appearRight} ${animationDuration}
          ${animationFunc} forwards}`
          : 'none'};
      `
  }
}

const Container = styled.div<
  SnackbarProps & {
    hasClose: boolean
    marginHorizontal: SnackbarProps['marginHorizontal']
    marginVertical: SnackbarProps['marginVertical']
    position: SnackbarProps['position']
  }
>`
  display: flex;
  align-items: flex-start;
  background: ${({ theme }) => theme.color.onPrimaryLight};
  border-radius: 8px;
  padding: ${({ hasClose }) => (hasClose ? '8px 8px 8px 12px' : '8px 12px')};
  position: fixed;
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
  max-width: 350px;
  overflow: hidden;
  ${({
    open = false,
    theme,
    severity = 'error',
    autoHideDuration,
    marginHorizontal = '12px',
    marginVertical = '96px',
    position,
  }) => css`
    color: ${theme.color[severityToColor[severity]]};
    border: 1px solid ${theme.color[severityToColor[severity]]};
    ${getPositionCSS(open, marginHorizontal, marginVertical, autoHideDuration, position)}
  `};
`

const CrossStyled = styled(IconCross)<SnackbarProps>`
  cursor: pointer;
  fill: ${({ theme, severity = 'error' }) => theme.color[severityToColor[severity]]};
  width: 10px;
  height: 10px;
  transition: opacity 0.2s ease;

  :hover {
    opacity: 0.8;
  }
`

const StyledButton = styled(Button)`
  padding: 4px;
  margin-left: 8px;
`

const Content = styled.div`
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 4;
  overflow: hidden;
`

export const Snackbar: React.FC<SnackbarProps> = ({
  open = true,
  children,
  onHide,
  hideCloseButton,
  autoHideDuration = hideCloseButton ? 5000 : 0,
  onClose,
  severity = 'error',
  marginHorizontal,
  marginVertical,
  position = 'bottomRight',
  ...props
}) => {
  useEffect(() => {
    if (autoHideDuration && open) {
      const timer = setTimeout(() => {
        onHide?.()
      }, autoHideDuration)

      return () => {
        clearTimeout(timer)
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const showClose = !!onClose && !hideCloseButton

  return (
    <Wrapper key={String(open)} data-test-id="notification-popup" {...props}>
      <Container
        open={open}
        autoHideDuration={autoHideDuration}
        severity={severity}
        hasClose={showClose}
        marginHorizontal={marginHorizontal}
        marginVertical={marginVertical}
        position={position}
      >
        <Content>{children}</Content>
        {showClose && (
          <StyledButton onClick={onClose} variant="no-style">
            <CrossStyled severity={severity} />
          </StyledButton>
        )}
      </Container>
    </Wrapper>
  )
}

export default Snackbar
